import React, {useState} from 'react';
import clsx from "clsx";
import {motion} from "framer-motion";
import Button from "../../elements/Button/Button";
import styles from "../../FormController/components/Form/Form.module.scss";
import registerStyles from './Register.module.scss'
import {useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import formValidation from "../../../utilities/formValidation";
import {setErrors} from "../../../store/actions/errorsActions";
import {registerRequest} from "../../../services/auth";
import {registerSuccess, registerUser} from "../../../store/actions/registerActions";
import toast from "react-hot-toast";
import Info from "../../elements/Info/Info";

const Register = () => {
    document.title = 'Register | Invoice App';
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const registerErrors = useSelector((state) => state.errors.register);
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        email: '',
        password: '',
        confirmPassword: '',
    });

    const handleChange = (event) => {
        const {name, value} = event.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const validationErrors = formValidation(formData);
        if (validationErrors.length > 0) {
            console.log(validationErrors);
            dispatch(setErrors('register', 'Invalid form input', validationErrors));
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            dispatch(setErrors('register', 'Passwords do not match', ['Passwords do not match']));
            return;
        }

        try {
            dispatch(registerUser());
            const response = await registerRequest(formData);
            dispatch(registerSuccess(response));
            toast.success('Registration successful');
            navigate('/users');
        } catch (error) {
            const errorMessage =
                error.response?.data?.meta?.message || "Registration failed. Please try again.";
            dispatch(setErrors('register', error.response?.data?.errors || {}, [errorMessage]));
            toast.error(errorMessage);
        }
    };

    const handleBack = () => {
        navigate('/login');
    }

    return (
        <motion.div
            className={registerStyles.registerBg}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{
                duration: 1,
                ease: "easeOut",
                bounce: 0.3,
            }}
        >
            <motion.div
                className={registerStyles.shape1}
                animate={{
                    scale: [1, 1.15, 1],
                    rotate: [0, 360],
                }}
                transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "linear",
                }}
            />
            <motion.div
                className={registerStyles.shape2}
                animate={{
                    scale: [1, 1.15, 1],
                    rotate: [0, -360],
                }}
                transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "linear",
                }}
            />
            <motion.div
                className={registerStyles.formContainer}
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                transition={{
                    duration: 1,
                    ease: "easeOut",
                    bounce: 0.3,
                }}
            >
                <div className={registerStyles.headerContainer}>
                    <h2 className={styles.title}>
                        Create an account
                    </h2>
                    <Info paragraphs={[
                        'Account may contain sensitive information. ',
                        'Make sure password is secure.',
                        'Password must be at least 8 characters long',
                        'And includes a number and a special character'
                    ]}
                    />
                </div>
                <form onSubmit={(event) => handleSubmit(event)}>
                    <div className={registerStyles.inputGroup}>
                        <div className={styles.inputContainer}>
                            <label
                                className={clsx(styles.label, {
                                    [styles.error]: registerErrors.name,
                                })}
                                htmlFor="name"
                            >
                                Name
                                {registerErrors.name && (
                                    <span className={styles.error}>Can't be empty</span>
                                )}
                            </label>
                            <input
                                className={clsx(styles.input, {
                                    [styles.error]: registerErrors.name,
                                })}
                                type="name"
                                name="name"
                                value={formData.name}
                                onChange={(event) => handleChange(event)}
                            />
                        </div>
                        <div className={styles.inputContainer}>
                            <label
                                className={clsx(styles.label, {
                                    [styles.error]: registerErrors.surname,
                                })}
                                htmlFor="surname"
                            >
                                Surname
                                {registerErrors.surname && (
                                    <span className={styles.error}>Can't be empty</span>
                                )}
                            </label>
                            <input
                                className={clsx(styles.input, {
                                    [styles.error]: registerErrors.surname,
                                })}
                                type="surname"
                                name="surname"
                                value={formData.surname}
                                onChange={(event) => handleChange(event)}
                            />
                        </div>
                    </div>
                    <div className={styles.inputContainer}>
                        <label
                            htmlFor="email"
                            className={clsx(styles.label, {
                                [styles.error]: registerErrors.email,
                            })}
                        >
                            Email
                            {registerErrors.email && (
                                <span className={styles.error}>Can't be empty</span>
                            )}
                        </label>
                        <input
                            className={clsx(styles.input, {
                                [styles.error]: registerErrors.email
                            })}
                            type="text"
                            name="email"
                            value={formData.email}
                            placeholder="manager@gamrise.com"
                            onChange={(event) => handleChange(event)}
                        />
                    </div>
                    <div className={styles.inputContainer}>
                        <label
                            className={clsx(styles.label, {
                                [styles.error]: registerErrors.password,
                            })}
                            htmlFor="password"
                        >
                            Password
                            {registerErrors.password && (
                                <span className={styles.error}>Can't be empty</span>
                            )}
                        </label>
                        <input
                            className={clsx(styles.input, {
                                [styles.error]: registerErrors.password,
                            })}
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={(event) => handleChange(event)}
                        />
                    </div>
                    <div className={styles.inputContainer}>
                        <label
                            className={clsx(styles.label, {
                                [styles.error]: registerErrors.confirmPassword,
                            })}
                            htmlFor="confirmPassword"
                        >
                            Confirm Password
                            {registerErrors.confirmPassword && (
                                <span className={styles.error}>Can't be empty</span>
                            )}
                        </label>
                        <input
                            className={clsx(styles.input, {
                                [styles.error]: registerErrors.confirmPassword,
                            })}
                            type="password"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={(event) => handleChange(event)}
                        />
                    </div>
                    <div className={registerStyles.buttonContainer}>
                        <Button className='secondary' type='button' onClick={handleBack}>Back</Button>
                        <Button
                            className='primary'
                            type="submit"
                            disabled={!formData.name || !formData.surname || !formData.email || !formData.password || !formData.confirmPassword}
                        >
                            Register
                        </Button>
                    </div>
                </form>
                {registerErrors.msg && registerErrors.msg.length > 0 && (
                    <div className={registerStyles.errorContainer}>
                        {Array.isArray(registerErrors.msg) && registerErrors.msg.map((item, index) => (
                            <span key={index} className={styles.error}>{item}</span>
                        ))}
                    </div>
                )}
            </motion.div>
        </motion.div>
    );
};

export default Register;
